import { inject, Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { every, omit } from '@sama/micro-dash';
import {
  getShapeForEventTracking,
  ShapeState,
} from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  ensureKeyLocation,
  findInsertPoint,
  FrameItem,
  isKeyLocation,
  removeKeyLocationIfNotLast,
} from '../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { selectionToArray } from '../../../../../cross-team-shared/misc-utils';
import { ObjectWith } from '../../../../../cross-team-shared/types/misc';
import { outputAnnotationInfoStoreToken } from '../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { ContentConnector } from '../../../../../team-apis/content/content-connector';
import {
  PointType,
  ShapeLocation,
} from '../../../../../team-apis/otterdam/answers/shape-typings';
import {
  getMlExtrapolationEventTrackingValues,
  updateExtrapolationsForShapes,
} from '../../../../shared/utils/extrapolation-utils';
import { contentConnectorToken } from '../../utils/misc-utils';
import { OutputKeyboardShortcutService } from './output-keyboard-shortcut.service';
import { PushAndTrackService } from './push-and-track.service';
import { ShapeSelectionService } from './shape-selection.service';

interface KeyframeEventTrackingValue {
  source: string;
  method: 'copy_last_keyframe' | 'toggle_keyframe';
}

@Injectable()
export class KeyframeService {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );

  constructor(
    protected keyboardShortcutService: OutputKeyboardShortcutService,
    private store: AbstractAnnotationStore,
    @Inject(contentConnectorToken)
    private contentConnector: ContentConnector,
    private pushAndTrackService: PushAndTrackService,
    private selectionService: ShapeSelectionService,
  ) {}

  toggleKeyframesForSelectedShapes(source: string) {
    const selectedShapeStores = this.getSelectionIndexes()
      .filter((index: number) => index !== 0) // Ignore in-progress shape
      .map((index: number) => this.shapesStore(index));
    const shouldRemoveKeyframes =
      // @ts-ignore -- fix me
      this.allShapesHaveKeyframe(selectedShapeStores);
    // @ts-ignore -- fix me
    this.applyNewKeyframeState(shouldRemoveKeyframes, selectedShapeStores, {
      source,
      method: 'toggle_keyframe',
    });
  }

  copyLastKeyframeForSelectedShapes(source: string) {
    const indexesSelection = this.getSelectionIndexes();
    const frameNumber = this.store.state().frameNumber;
    const validShapeStores = indexesSelection // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      .filter((shapeIndex) => !!this.getLastKeyframe(shapeIndex, frameNumber))
      .map((index) => this.shapesStore(index));

    // @ts-ignore -- fix me
    this.applyNewKeyframeState(false, validShapeStores, {
      source,
      method: 'copy_last_keyframe',
    });
  }

  private getSelectionIndexes() {
    const selection = this.selectionService.getSelectionForOutput();
    return selectionToArray(selection);
  }

  private getLastKeyframe(shapeIndex: number, frameNumber: number) {
    // @ts-ignore -- fix me
    const keyLocations = this.shapesStore.state()[shapeIndex].key_locations;
    return keyLocations[findInsertPoint(keyLocations, frameNumber) - 1];
  }

  private applyNewKeyframeState(
    shouldRemoveKeyframes: boolean,
    selectedShapeStores: Array<Store<ShapeState>>,
    { source, method }: KeyframeEventTrackingValue,
  ) {
    const selectedShapeState = selectedShapeStores.map((shapeStore) =>
      shapeStore.state(),
    );
    let extrapolatedFrameCounts;
    this.store.batch(() => {
      if (shouldRemoveKeyframes) {
        selectedShapeStores.forEach((store) =>
          removeKeyLocationIfNotLast(store, this.store.state().frameNumber),
        );
      } else {
        extrapolatedFrameCounts =
          this.addKeyframesAndFetchExtrapolatedLocations(
            selectedShapeStores,
            method,
          );
      }
    });

    this.trackToggleKeyframeEvent(
      shouldRemoveKeyframes,
      selectedShapeState,
      { source, method },
      extrapolatedFrameCounts,
    );
  }

  private addKeyframesAndFetchExtrapolatedLocations(
    selectedShapeStores: Array<Store<ShapeState>>,
    method: 'copy_last_keyframe' | 'toggle_keyframe',
  ) {
    const frameNumber = this.store.state().frameNumber;
    const validShapesForMlExtrapolation = this.getShapeIndicesNotKeyframe(
      selectedShapeStores,
      frameNumber,
    );
    this.addKeyframes(selectedShapeStores, method, frameNumber);
    return updateExtrapolationsForShapes(
      validShapesForMlExtrapolation,
      () => this.contentConnector,
      frameNumber,
    );
  }

  private addKeyframes(
    selectedShapeStores: Array<Store<ShapeState>>,
    method: 'copy_last_keyframe' | 'toggle_keyframe',
    frameNumber: number,
  ) {
    selectedShapeStores.forEach((store) => {
      let specifiedLocation: ShapeLocation<PointType> | undefined;
      if (method === 'copy_last_keyframe') {
        const shapeLocationWithoutExtrapolatedPoints = omit(
          this.getLastKeyframe(store.state().index, frameNumber),
          'extrapolation',
        ) as ShapeLocation<PointType>;
        specifiedLocation = {
          ...shapeLocationWithoutExtrapolatedPoints,
          frame_number: frameNumber,
        };
      }

      ensureKeyLocation(
        store,
        frameNumber,
        this.contentConnector,
        specifiedLocation,
      );
    });
  }

  private allShapesHaveKeyframe(selectedShapeStores: Array<Store<ShapeState>>) {
    const selectedShapes = selectedShapeStores.map((store) => store.state());
    const frameNumber = this.store.state().frameNumber;
    return every(selectedShapes, (shape) =>
      isKeyLocation(shape.key_locations, frameNumber),
    );
  }

  private trackToggleKeyframeEvent(
    shouldRemoveKeyframes: boolean,
    selectedShapeState: ShapeState[],
    { source, method }: KeyframeEventTrackingValue,
    requestedExtrapolatedFramesCounts?: ObjectWith<number | undefined>,
  ) {
    const affectedShapeStores = this.getAffectedShapeStores(
      shouldRemoveKeyframes,
      selectedShapeState,
    );
    const shapes = this.getShapesEventTrackingValues(
      affectedShapeStores,
      requestedExtrapolatedFramesCounts,
    );
    this.pushAndTrackService.pushAndTrack({
      id: 'edit_shape_points',
      bucket: 'adjustment',
      source,
      method,
      is_now_keyframe: !shouldRemoveKeyframes,
      edited_shapes_count: affectedShapeStores.length,
      shapes,
    });
  }

  private getAffectedShapeStores(
    shouldRemove: boolean,
    selectedShapeStores: ShapeState[],
  ) {
    const frameNumber = this.store.state().frameNumber;
    return selectedShapeStores.filter((shapeStore) => {
      const keyLocations = shapeStore.key_locations;
      if (shouldRemove) {
        return this.hasRemovableKeyframe(keyLocations, frameNumber);
      } else {
        return !isKeyLocation(keyLocations, frameNumber);
      }
    });
  }

  private hasRemovableKeyframe(keyLocations: FrameItem[], frameNumber: number) {
    return keyLocations.length > 1 && isKeyLocation(keyLocations, frameNumber);
  }

  private getShapesEventTrackingValues(
    shapeStates: ShapeState[],
    requestedExtrapolatedFramesCounts?: ObjectWith<number | undefined>,
  ) {
    const shapes = shapeStates.map((shape) => getShapeForEventTracking(shape));
    if (requestedExtrapolatedFramesCounts) {
      return getMlExtrapolationEventTrackingValues(
        requestedExtrapolatedFramesCounts,
        shapes,
      );
    }
    return shapes;
  }

  private getShapeIndicesNotKeyframe(
    shapeStores: Array<Store<ShapeState>>,
    frameNumber: number,
  ) {
    return shapeStores
      .filter(
        (shapeStore) =>
          !isKeyLocation(shapeStore.state().key_locations, frameNumber),
      )
      .map((shapeStore) => shapeStore.state().index);
  }
}
